import type { ModalProps } from "@replohq/lattice/components/modal/Modal";

import * as React from "react";

import { Drawer } from "@replohq/lattice/components/drawer/Drawer";
import {
  ModalContent,
  ModalDescription,
  ModalHeader,
  ModalRoot,
  ModalTitle,
  ModalTrigger,
} from "@replohq/lattice/components/modal/Modal";
import { useIsMobile } from "@replohq/lattice/hooks/use-mobile";
import { cn } from "@replohq/lattice/utils/cn";

export interface SplitModalProps extends Omit<ModalProps, "onOpenChange"> {
  media: React.ReactNode;
  accessibleTitle?: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
  /**
   * Fraction of the modal width given to the media panel (0–1); the rest goes to
   * the content panel. When set, the grid fills the modal width and strictly
   * honors this ratio. When omitted, the modal keeps its legacy even split.
   */
  mediaWidthFraction?: number;
  contentProps?: {
    style?: React.CSSProperties;
    "data-agent-theme"?: string;
    "data-replo-surface"?: string;
  };
}

function SplitModal({
  media,
  accessibleTitle,
  title,
  description,
  children,
  footer,
  open,
  onOpenChange,
  dismissable = true,
  showCloseButton = true,
  size,
  className,
  UNSAFE_className,
  trigger,
  contentProps,
  mediaWidthFraction,
}: SplitModalProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        accessibleTitle={accessibleTitle}
        title={title}
        description={description}
        dismissable={dismissable}
        footer={footer}
        contentProps={contentProps}
      >
        {children}
      </Drawer>
    );
  }

  return (
    <ModalRoot
      open={open}
      onOpenChange={onOpenChange ? (isOpen) => onOpenChange(isOpen) : undefined}
      disablePointerDismissal={!dismissable}
    >
      {trigger && <ModalTrigger>{trigger}</ModalTrigger>}
      <ModalContent
        showCloseButton={showCloseButton}
        size={size}
        className={className}
        UNSAFE_className={cn("p-0 overflow-hidden", UNSAFE_className)}
        {...contentProps}
      >
        {!title && accessibleTitle && (
          <div className="sr-only">
            <ModalTitle>{accessibleTitle}</ModalTitle>
          </div>
        )}
        <div
          className={cn(
            "grid",
            mediaWidthFraction === undefined ? "grid-cols-[1fr_1fr]" : "w-full",
          )}
          style={
            mediaWidthFraction === undefined
              ? undefined
              : {
                  // NOTE (Yuxin, 2026-07-26): minmax(0, …fr) lets each column
                  // shrink below its content's intrinsic width so the ratio is
                  // honored; plain fr columns let a wide media panel refuse to
                  // shrink and squeeze the content half.
                  gridTemplateColumns: `minmax(0, ${mediaWidthFraction}fr) minmax(0, ${
                    1 - mediaWidthFraction
                  }fr)`,
                }
          }
        >
          <div className="flex min-w-0 items-center justify-center">
            {media}
          </div>

          <div
            className={cn(
              "flex flex-col justify-between gap-8",
              size === "lg" ? "p-8" : "p-6",
            )}
          >
            {(title || description) && (
              <ModalHeader className={showCloseButton ? "pr-10" : undefined}>
                <div className="flex items-center justify-between gap-2">
                  {title && <ModalTitle>{title}</ModalTitle>}
                </div>
                {description && (
                  <ModalDescription>{description}</ModalDescription>
                )}
              </ModalHeader>
            )}
            {children}
            {footer && <div className="flex flex-col gap-3">{footer}</div>}
          </div>
        </div>
      </ModalContent>
    </ModalRoot>
  );
}

export { SplitModal };
